import { Injectable, OnDestroy, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { EMPTY, Subject, timer } from 'rxjs';
import { distinctUntilChanged, map, pairwise, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { VendorOrdersService } from '../core/services/vendor-orders.service';
import {
  VENDOR_REMINDER_SOUND,
  VendorLocalNotificationService,
} from './vendor-local-notification.service';
import { VendorNativeAlarmService } from './vendor-native-alarm.service';

const REMINDER_INTERVAL_MS = 10_000;
/** Skip reminder if new-order fanfare just played (avoid double sound). */
const FANFARE_COOLDOWN_MS = 3_000;

/**
 * Repeats order reminders every 10s while the vendor has unaccepted new orders.
 * - Foreground: Web Audio / HTML5 audio + toast (web) or native alarm clip (Android/iOS)
 * - Screen off / background: scheduled local notifications with custom alarm sound (res/raw)
 */
@Injectable({ providedIn: 'root' })
export class PendingOrderReminderService implements OnDestroy {
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly localNotifications = inject(VendorLocalNotificationService);
  private readonly nativeAlarm = inject(VendorNativeAlarmService);
  private readonly destroy$ = new Subject<void>();

  private listenersSetup = false;
  private audioContext: AudioContext | null = null;
  private pausedByUser = false;
  private fanfarePlayedAt = 0;
  private pendingCount = 0;
  private appInForeground = true;
  private screenVisible = true;
  private readonly isNative = Capacitor.isNativePlatform();

  start(): void {
    this.setupAudioUnlock();
    this.setupAppStateListener();
    this.setupVisibilityListener();
    this.setupReminderDeliveredChain();
    this.setupReminderLoop();
    if (this.isNative) {
      void this.localNotifications.ensureReady();
    }
  }

  /** User dismissed toast/browser notification — stop repeating until next new order or all accepted. */
  pauseReminders(): void {
    this.pausedByUser = true;
    this.nativeAlarm.stopReminder();
    if (this.isNative) {
      void this.localNotifications.cancelReminderNotifications();
    }
  }

  /** Called when a new-order fanfare plays so the reminder loop does not double-chime. */
  markFanfarePlayed(): void {
    this.fanfarePlayedAt = Date.now();
  }

  /** Stop sounds and cancel queued local notifications immediately. */
  async stopAllReminders(): Promise<void> {
    this.pendingCount = 0;
    this.nativeAlarm.stopReminder();
    if (this.isNative) {
      await this.localNotifications.cancelReminderNotifications();
    }
  }

  private isInteractiveForeground(): boolean {
    return this.appInForeground && this.screenVisible;
  }

  private setupAppStateListener(): void {
    if (!this.isNative) {
      return;
    }
    void App.addListener('appStateChange', ({ isActive }) => {
      this.appInForeground = isActive;
      if (isActive) {
        void this.localNotifications.cancelBackgroundReminders();
        return;
      }
      void this.onBackgroundOrScreenOff();
    });
  }

  private setupVisibilityListener(): void {
    if (typeof document === 'undefined') {
      return;
    }
    const onVisibility = () => {
      this.screenVisible = document.visibilityState === 'visible';
      if (!this.screenVisible) {
        void this.onBackgroundOrScreenOff();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    this.destroy$.subscribe(() => document.removeEventListener('visibilitychange', onVisibility));
  }

  /** Re-schedule the next batch when a background notification fires (screen locked). */
  private setupReminderDeliveredChain(): void {
    if (!this.isNative) {
      return;
    }
    this.localNotifications.reminderDelivered$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        void this.onBackgroundReminderDelivered();
      });
  }

  private async onBackgroundOrScreenOff(): Promise<void> {
    if (this.pausedByUser) {
      return;
    }
    if (Date.now() - this.fanfarePlayedAt < FANFARE_COOLDOWN_MS) {
      return;
    }
    if (await this.isNewOrderRingPlaying()) {
      return;
    }
    if (!(await this.refreshPendingCount())) {
      return;
    }
    await this.playNativeReminder(true);
    await this.scheduleNativeBackgroundReminders();
  }

  /** Re-read tab counts before chaining background reminders (avoids stale 1–2 extra alerts). */
  private async onBackgroundReminderDelivered(): Promise<void> {
    if (this.pausedByUser) {
      return;
    }
    if (this.isInteractiveForeground()) {
      return;
    }
    if (await this.isNewOrderRingPlaying()) {
      return;
    }
    if (!(await this.refreshPendingCount())) {
      await this.stopAllReminders();
      return;
    }
    await this.scheduleNativeBackgroundReminders();
  }

  private async refreshPendingCount(): Promise<boolean> {
    try {
      const counts = await this.vendorOrdersService.refreshTabCounts();
      this.pendingCount = counts.new;
      return counts.new > 0;
    } catch {
      return this.pendingCount > 0;
    }
  }

  private setupAudioUnlock(): void {
    if (typeof document === 'undefined' || this.isNative) {
      return;
    }
    const events = ['click', 'touchstart', 'keydown'];
    const doUnlock = () => {
      this.unlockAudio();
      events.forEach((e) => document.removeEventListener(e, doUnlock));
    };
    events.forEach((e) => document.addEventListener(e, doUnlock, { once: true, passive: true }));
  }

  private unlockAudio(): void {
    if (this.audioContext?.state === 'running') {
      return;
    }
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) {
        return;
      }
      const ctx = new Ctx();
      this.audioContext = ctx;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.01, ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch {
      // ignore
    }
  }

  private setupReminderLoop(): void {
    if (this.listenersSetup) {
      return;
    }
    this.listenersSetup = true;

    this.vendorOrdersService.vendorTabCounts$
      .pipe(
        map((c) => c.new),
        startWith(0),
        pairwise(),
        tap(([prev, curr]) => {
          this.pendingCount = curr;
          if (curr <= 0) {
            this.pausedByUser = false;
            void this.stopAllReminders();
          } else if (curr > prev) {
            this.pausedByUser = false;
            // New-order count just rose: NewOrderAlertService owns the ring.
            // Stamp cooldown here so timer(0) cannot beat markFanfarePlayed().
            this.fanfarePlayedAt = Date.now();
            if (this.isNative && !this.isInteractiveForeground()) {
              void this.onBackgroundOrScreenOff();
            }
          } else if (curr > 0 && this.isNative && !this.isInteractiveForeground()) {
            void this.scheduleNativeBackgroundReminders();
          }
        }),
        map(([, curr]) => curr > 0),
        distinctUntilChanged(),
        switchMap((hasPending) => (hasPending ? timer(0, REMINDER_INTERVAL_MS) : EMPTY)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => void this.playReminder());
  }

  private shouldPlayReminder(): boolean {
    if (this.pausedByUser) {
      return false;
    }
    if (this.nativeAlarm.isRinging) {
      return false;
    }
    if (Date.now() - this.fanfarePlayedAt < FANFARE_COOLDOWN_MS) {
      return false;
    }
    return this.pendingCount > 0;
  }

  /** True while the continuous new-order ring is looping (JS or native FCM/FGS). */
  private async isNewOrderRingPlaying(): Promise<boolean> {
    return this.nativeAlarm.isRingPlaying();
  }

  private async playReminder(): Promise<void> {
    if (!this.shouldPlayReminder()) {
      return;
    }
    if (await this.isNewOrderRingPlaying()) {
      return;
    }
    if (!(await this.refreshPendingCount())) {
      await this.stopAllReminders();
      return;
    }
    if (await this.isNewOrderRingPlaying()) {
      return;
    }

    if (this.isNative) {
      await this.playNativeReminder(false);
      return;
    }

    this.playWebReminder();
  }

  private async playNativeReminder(forceNotify: boolean): Promise<void> {
    if (await this.isNewOrderRingPlaying()) {
      return;
    }
    const count = this.pendingCount;
    const title = count === 1 ? 'Pending order waiting' : `${count} pending orders`;
    const body = 'Tap to open Live Orders and accept';
    const extra = this.buildReminderNotificationExtra();

    if (this.isInteractiveForeground()) {
      this.nativeAlarm.playPendingReminder();
    } else if (forceNotify || !this.isInteractiveForeground()) {
      await this.localNotifications.show({
        title,
        body,
        sound: VENDOR_REMINDER_SOUND,
        ongoing: count > 0,
        extra,
      });
    }

    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch {
      // ignore
    }

    if (!this.isInteractiveForeground()) {
      await this.scheduleNativeBackgroundReminders();
    }
  }

  private async scheduleNativeBackgroundReminders(): Promise<void> {
    if (this.pausedByUser) {
      return;
    }
    if (await this.isNewOrderRingPlaying()) {
      return;
    }
    if (!(await this.refreshPendingCount())) {
      await this.stopAllReminders();
      return;
    }
    const count = this.pendingCount;
    const title = count === 1 ? 'Pending order reminder' : `${count} pending orders`;
    const body = 'Unaccepted orders need your attention — tap to accept';
    await this.localNotifications.scheduleBackgroundReminders(
      REMINDER_INTERVAL_MS,
      title,
      body,
      this.buildReminderNotificationExtra(),
    );
  }

  private buildReminderNotificationExtra(): Record<string, string> {
    const extra: Record<string, string> = { type: 'order-reminder' };
    const firstPending = this.vendorOrdersService.getFirstPendingOrder();
    if (firstPending?.id) {
      extra['orderId'] = firstPending.id;
    }
    if (firstPending?.orderNumber != null) {
      extra['orderNumber'] = String(firstPending.orderNumber);
    }
    return extra;
  }

  private playWebReminder(): void {
    this.nativeAlarm.startContinuousRing(0.95);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.audioContext?.state !== 'closed') {
      this.audioContext?.close();
    }
    if (this.isNative) {
      void this.localNotifications.cancelReminderNotifications();
    }
  }
}
